package org.teddapp.project.ui.vault

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.tedd.db.Keychain
import com.tedd.db.Keychain_item
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.launch
import kotlinx.coroutines.withContext
import org.teddapp.project.db.TeddRepositories
import org.teddapp.project.ui.TeddSnackbarState
import org.teddapp.project.vault.Vault
import org.teddapp.project.vault.VaultCipher
import org.teddapp.project.vault.VaultEntry
import org.teddapp.project.vault.VaultFormat

/** Une entrée ouverte : sa ligne en base et son contenu déchiffré. */
internal class OpenEntry(val row: Keychain_item, val entry: VaultEntry)

/** Ce qu'on est en train d'écrire : une entrée neuve (existing = null) ou une entrée reprise. */
internal class EntryDraft(val existing: Keychain_item?, initial: VaultEntry) {
    var title by mutableStateOf(initial.title)
    var username by mutableStateOf(initial.username)
    var secret by mutableStateOf(initial.secret)
    var url by mutableStateOf(initial.url)
    var notes by mutableStateOf(initial.notes)
    var content by mutableStateOf(initial.content)
    var format by mutableStateOf(VaultFormat.of(initial.format))

    /** Pose un texte collé dans le bloc et en devine le format. */
    fun paste(text: String) {
        content = text
        format = VaultFormat.guess(text)
    }

    // Le bloc est gardé à l'identique, espaces et retours compris : c'est ce qu'on recopiera.
    fun toEntry() = VaultEntry(title.trim(), username.trim(), secret, url.trim(), notes.trim(), content, if (content.isEmpty()) "" else format.key)
}

/**
 * État de l'écran Trousseau. Les clés des trousseaux ouverts ne vivent qu'ici, en mémoire :
 * quitter l'écran les oublie, et tout est de nouveau verrouillé au retour.
 */
internal class VaultModel(private val repos: TeddRepositories, private val scope: CoroutineScope) {
    private companion object { const val KEY_DEVICE_SECRET = "vault.device_secret" }

    var keychains: List<Keychain> by mutableStateOf(emptyList())
    var loaded by mutableStateOf(false)
    var items: List<Keychain_item> by mutableStateOf(emptyList())

    private val keys = mutableStateMapOf<String, String>()

    var selectedKeychainId by mutableStateOf<String?>(null)
    var selectedItemId by mutableStateOf<String?>(null)
    var creating by mutableStateOf(false)
    var draft by mutableStateOf<EntryDraft?>(null)
    var search by mutableStateOf("")
    var busy by mutableStateOf(false)
    var error by mutableStateOf<String?>(null)
    var confirmDeleteItem by mutableStateOf<OpenEntry?>(null)
    var confirmDeleteKeychain by mutableStateOf<Keychain?>(null)
    val snackbar = TeddSnackbarState()

    val selectedKeychain: Keychain? get() = keychains.firstOrNull { it.id == selectedKeychainId } ?: keychains.firstOrNull()
    fun isUnlocked(id: String): Boolean = id in keys
    val unlocked: Boolean get() = selectedKeychain?.let { isUnlocked(it.id) } == true

    /** Les entrées du trousseau ouvert, déchiffrées. Une entrée illisible est ignorée. */
    fun openEntries(): List<OpenEntry> {
        val key = selectedKeychain?.let { keys[it.id] } ?: return emptyList()
        return items.mapNotNull { row -> Vault.openEntry(key, row.payload)?.let { OpenEntry(row, it) } }
    }

    fun select(id: String) {
        selectedKeychainId = id
        selectedItemId = null
        draft = null
        creating = false
        error = null
        search = ""
    }

    /**
     * Le secret de l'appareil, tiré au sort la première fois et gardé dans les préférences
     * locales. Il remplace la phrase secrète pour les trousseaux créés sans phrase.
     */
    private suspend fun deviceSecret(): String =
        repos.preferences.get(KEY_DEVICE_SECRET) ?: VaultCipher.newKey().also { repos.preferences.set(KEY_DEVICE_SECRET, it) }

    /** Sans phrase (vide), le trousseau s'ouvre tout seul sur cet appareil. */
    fun create(name: String, passphrase: String) {
        if (busy) return
        busy = true
        error = null
        scope.launch {
            try {
                val auto = passphrase.isBlank()
                val secret = if (auto) deviceSecret() else passphrase
                val vault = withContext(Dispatchers.Default) { Vault.create(secret, auto = auto) }
                val id = repos.keychains.createKeychain(name.ifBlank { "Personnel" }, vault.wrappedKey, vault.kdf)
                keys[id] = vault.key
                selectedKeychainId = id
                creating = false
            } finally {
                busy = false
            }
        }
    }

    fun unlock(passphrase: String) {
        val kc = selectedKeychain ?: return
        if (busy) return
        busy = true
        error = null
        scope.launch {
            try {
                val auto = Vault.isAuto(kc.kdf)
                val secret = if (auto) deviceSecret() else passphrase
                val key = withContext(Dispatchers.Default) { Vault.unlock(secret, kc.wrapped_key, kc.kdf) }
                if (key == null) {
                    error = if (auto) {
                        "Créé sans phrase sur un autre appareil, il ne s'ouvre que là-bas. Ouvre-le sur cet appareil-là et protège-le par une phrase : il s'ouvrira ici aussi."
                    } else "Phrase secrète incorrecte."
                } else keys[kc.id] = key
            } finally {
                busy = false
            }
        }
    }

    /** Vrai si le trousseau choisi s'ouvre sans phrase. */
    val selectedIsAuto: Boolean get() = selectedKeychain?.let { Vault.isAuto(it.kdf) } == true

    fun lock() {
        selectedKeychain?.let { keys.remove(it.id) }
        selectedItemId = null
        draft = null
    }

    fun newEntry() { draft = EntryDraft(null, VaultEntry()) }

    /** Nouvelle entrée partant de ce qui est dans le presse-papiers. */
    fun newEntryFrom(pasted: String) {
        draft = EntryDraft(null, VaultEntry()).also { it.paste(pasted) }
    }
    fun edit(e: OpenEntry) { draft = EntryDraft(e.row, e.entry) }

    fun saveDraft() {
        val d = draft ?: return
        val kc = selectedKeychain ?: return
        val key = keys[kc.id] ?: return
        val payload = Vault.sealEntry(key, d.toEntry())
        scope.launch {
            if (d.existing == null) selectedItemId = repos.keychains.createItem(kc.id, payload)
            else repos.keychains.updateItem(d.existing.id, payload)
            draft = null
            snackbar.show("Entrée enregistrée")
        }
    }

    fun deleteItem(e: OpenEntry) {
        scope.launch {
            repos.keychains.deleteItem(e.row.id)
            if (selectedItemId == e.row.id) selectedItemId = null
            confirmDeleteItem = null
            snackbar.show("Entrée supprimée")
        }
    }

    fun deleteKeychain(kc: Keychain) {
        scope.launch {
            repos.keychains.deleteKeychain(kc.id)
            keys.remove(kc.id)
            if (selectedKeychainId == kc.id) selectedKeychainId = null
            confirmDeleteKeychain = null
            snackbar.show("Trousseau supprimé")
        }
    }

    /**
     * Protège par une phrase un trousseau ouvert qui n'en avait pas : il s'ouvre alors sur
     * tous les appareils du compte, plus seulement sur celui qui l'a créé.
     */
    fun protect(passphrase: String) {
        val kc = selectedKeychain ?: return
        val key = keys[kc.id] ?: return
        if (busy) return
        busy = true
        scope.launch {
            try {
                val (wrapped, kdf) = withContext(Dispatchers.Default) { Vault.rewrap(key, passphrase) }
                repos.keychains.rewrapKeychain(kc.id, wrapped, kdf)
                snackbar.show("Protégé : il s'ouvre maintenant partout avec sa phrase")
            } finally {
                busy = false
            }
        }
    }

    /** Un secret tiré au hasard par le générateur du système : 24 caractères. */
    fun generateSecret(): String = VaultCipher.newKey().filter { it.isLetterOrDigit() }.take(24)
}

@Composable
internal fun rememberVaultModel(repos: TeddRepositories): VaultModel {
    val scope = rememberCoroutineScope()
    val model = remember(repos) { VaultModel(repos, scope) }
    val keychains by remember(repos) { repos.keychains.observeKeychains() }.collectAsState(null)
    model.keychains = keychains ?: emptyList()
    model.loaded = keychains != null
    val current = model.selectedKeychain?.id
    val items by remember(repos, current) { current?.let { repos.keychains.observeItems(it) } ?: flowOf(emptyList()) }.collectAsState(emptyList())
    model.items = items
    return model
}
